/* ════════════════════════════════════════════════════════════════════════
   DOWNLOAD-GUTHABEN — eine Quelle der Wahrheit: Firestore.
   ────────────────────────────────────────────────────────────────────────
   Das Guthaben ("Credits" = freie Downloads) hängt am KONTO, nicht am
   Browser. Es liegt ausschließlich in users/{uid}.credits auf dem Server.
   Damit gilt es geräteübergreifend und überlebt abgelehnte Cookies,
   geleerten Speicher, Inkognito-Fenster und Browserwechsel — im Gegensatz
   zu localStorage/Cookies, die genau dann verschwinden, wenn jemand der
   Analyse widerspricht oder privat surft (§ 25 TDDDG lässt uns nur
   technisch notwendigen Zugriff aufs Endgerät ohne Einwilligung).

   Zwei Regeln, die hier durchgesetzt werden:
     1. Jedes NEU angelegte Konto bekommt FREE_DOWNLOADS Downloads — aber
        gutgeschrieben wird ausschließlich vom Server (/claim-free-downloads).
        Diese Datei legt nur das Konto an und fragt danach.
     2. Ein Download kostet genau einen davon — abgebucht in einer
        Transaktion gegen den Serverstand, nie gegen den lokalen State.
        Ohne Guthaben wird nichts erzeugt; schlägt die Erzeugung fehl,
        wird zurückgebucht.

   Warum die Gutschrift nicht mehr hier passiert: Sie stand vorher als
   `credits: FREE_DOWNLOADS` direkt im Anlege-Schreibvorgang. Ein
   Inkognito-Fenster oder ein neu installierter Browser setzt zwar kein
   Guthaben zurück — das hängt am Konto, nicht am Gerät — wohl aber die
   Google-Anmeldung. Mit dem nächsten Google-Konto lief die Gutschrift
   also erneut. Der Server prüft jetzt zusätzlich Konto-Historie und
   Anschluss; siehe /claim-free-downloads im Backend.
   ════════════════════════════════════════════════════════════════════════ */

import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "./firebase";

const BACKEND_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "https://schriftbot-backend.vercel.app";

// Wie viele Gratis-Downloads ein neues Konto bekommt, steht bewusst NUR im
// Backend (FREE_DOWNLOADS in schriftbot-backend/index.js). Eine zweite Zahl
// hier wäre entweder überflüssig oder irgendwann falsch — und im Browser
// ohnehin nur eine Behauptung. Was wirklich gutgeschrieben wurde, sagt die
// Antwort von /claim-free-downloads.

/**
 * Holt die Gratis-Downloads beim Server ab. Der Server entscheidet, ob es sie
 * gibt: einmal pro Konto, und nur begrenzt oft pro Internetanschluss und Tag.
 * Idempotent — ein zweiter Aufruf gibt "already-claimed" zurück.
 *
 * @returns {Promise<{granted: number, credits: number, reason: string}>}
 *          reason: "granted" | "already-claimed" | "not-eligible"
 *                | "throttled" | "unreachable"
 */
async function claimFreeDownloads(user) {
  try {
    const idToken = await user.getIdToken();
    const response = await fetch(`${BACKEND_BASE_URL}/claim-free-downloads`, {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (error) {
    // Server nicht erreichbar: Das Konto steht, `freeDownloadsPending` bleibt
    // gesetzt — der nächste Login fragt einfach nochmal.
    console.error("Gratis-Downloads konnten nicht abgeholt werden:", error);
    return { granted: 0, credits: 0, reason: "unreachable" };
  }
}

/**
 * Legt das users-Dokument beim ersten Login an und schreibt dabei die
 * Gratis-Downloads gut. Bei bereits vorhandenen Konten werden nur Name und
 * E-Mail aufgefrischt — die Gutschrift passiert genau einmal, beim Anlegen.
 * Die Transaktion macht das idempotent: parallele Aufrufe (z. B. Login in
 * zwei Tabs) können nicht doppelt gutschreiben.
 *
 * @returns {Promise<{isNewUser: boolean, granted: number, credits: number}|null>}
 */
export async function ensureUserDocument(user) {
  if (!user) return null;
  const ref = doc(db, "users", user.uid);

  let account;
  try {
    account = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);

      if (!snap.exists()) {
        tx.set(ref, {
          uid: user.uid,
          email: user.email,
          name: user.displayName,
          // Kein Guthaben aus dem Browser heraus. Das Flag ist nur die
          // Anfrage "dieses Konto ist neu" — gutschreiben darf allein der
          // Server, der Konto-Historie und Anschluss dagegenhält.
          credits: 0,
          freeDownloadsPending: true,
          isUnlimited: false,
          plan: "",
          hasCustomFont: false,
          createdAt: serverTimestamp(),
        });
        return { isNewUser: true, pending: true, credits: 0 };
      }

      const data = snap.data();
      tx.update(ref, { email: user.email, name: user.displayName });
      return {
        isNewUser: false,
        // Offen gebliebene Gutschrift (z. B. Backend war beim Anlegen nicht
        // erreichbar) beim nächsten Login nachholen.
        pending: data.freeDownloadsPending === true,
        credits: data.credits || 0,
      };
    });
  } catch (error) {
    console.error("Konto konnte nicht angelegt/aktualisiert werden:", error);
    return null;
  }

  if (!account.pending) {
    return { ...account, granted: 0, reason: "already-claimed" };
  }

  const claim = await claimFreeDownloads(user);
  return {
    ...account,
    granted: claim.granted,
    credits: claim.credits || account.credits,
    reason: claim.reason,
  };
}

/**
 * Schickt eine Anfrage mit dem ID-Token des eingeloggten Nutzers ans Backend.
 * Alles, was Guthaben oder Münzen erhöht, läuft so: Die Firestore-Regeln
 * lassen den Browser diese Felder nicht mehr schreiben (sonst könnte sich
 * jeder über die Konsole Downloads oder ein Abo eintragen).
 *
 * @returns {Promise<object|null>} Antwort des Servers, null bei Fehlern
 */
export async function postToBackend(path, body) {
  const user = getAuth().currentUser;
  if (!user) return null;
  try {
    const idToken = await user.getIdToken();
    const response = await fetch(`${BACKEND_BASE_URL}${path}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body || {}),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error(`Backend-Aufruf ${path} fehlgeschlagen:`, error);
    return null;
  }
}

/**
 * Bucht einen Download ab — vor der Erzeugung, damit nie eine Datei
 * herausgeht, die nicht bezahlt (oder gratis gedeckt) ist. Der Server bucht
 * in einer Transaktion gegen den gespeicherten Stand: Doppelklicks oder zwei
 * offene Tabs können denselben Credit nicht zweimal ausgeben, und der Stand
 * kann nicht unter 0 rutschen.
 *
 * @returns {Promise<{ok: boolean, spent: boolean, credits: number, debitId?: string, reason?: string}>}
 *          reason: "signed-out" | "no-account" | "empty" | "error"
 *          debitId: für refundDownloadCredit, falls die Erzeugung scheitert
 */
export async function spendDownloadCredit() {
  if (!getAuth().currentUser) {
    return { ok: false, spent: false, credits: 0, reason: "signed-out" };
  }
  const result = await postToBackend("/spend-download-credit");
  return result || { ok: false, spent: false, credits: 0, reason: "error" };
}

/**
 * Bucht einen zuvor abgebuchten Download zurück — für den Fall, dass die
 * PDF-/Bild-Erzeugung nach der Abbuchung scheitert. Niemand soll für eine
 * Datei zahlen, die er nie bekommen hat. Der Server bucht nur Abbuchungen
 * zurück, die er selbst gerade gemacht hat (debitId), und jede nur einmal.
 */
export async function refundDownloadCredit(debitId) {
  if (!debitId) return;
  await postToBackend("/refund-download-credit", { debitId });
}
